/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { FormattedMessage, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { AutoDismissFlag } from '@atlaskit/flag';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import SimpleTag from '@atlaskit/tag/simple-tag';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { getCompareBranchesDialogSlice } from 'src/redux/branches';
import { ComponentFlagId } from 'src/redux/flags/types';
import { BucketState } from 'src/types/state';
import { getBranchesFullNames } from 'src/utils/compare-branches';

import { messages } from './compare-branches-success-flag.i18n';

type Props = {
  id: ComponentFlagId;
  action: 'sync' | 'merge';
  sourceBranchName: string;
  destinationBranchName: string;
  intl: IntlShape;
};

const BranchWrapper = styled.span({
  '> span > span:first-child': {
    marginInline: '0',
  },
});

const SuccessFlag = ({
  intl,
  id,
  action,
  sourceBranchName,
  destinationBranchName,
  ...otherProps
}: Props) => (
  <AutoDismissFlag
    {...otherProps}
    id={id}
    icon={
      <CheckCircleIcon
        spacing="spacious"
        label="success"
        color={token('color.icon.success')}
      />
    }
    title={intl.formatMessage(messages[action].compareBranchesSuccessTitle)}
    description={
      <FormattedMessage
        {...messages[action].compareBranchesSuccessDescription}
        values={{
          sourceBranch: (
            <BranchWrapper>
              <Tooltip content={sourceBranchName} tag="span">
                <SimpleTag text={sourceBranchName} />
              </Tooltip>
            </BranchWrapper>
          ),
          destinationBranch: (
            <BranchWrapper>
              <Tooltip content={destinationBranchName} tag="span">
                <SimpleTag text={destinationBranchName} />
              </Tooltip>
            </BranchWrapper>
          ),
        }}
      />
    }
  />
);

const mapStateToProps = (state: BucketState) => {
  const {
    sourceBranchName,
    destinationBranchName,
    sourceRepositoryFullName,
    destinationRepositoryFullName,
    isMerge,
  } = getCompareBranchesDialogSlice(state);

  const fullNames = getBranchesFullNames(
    sourceBranchName,
    destinationBranchName,
    sourceRepositoryFullName,
    destinationRepositoryFullName
  );

  return {
    action: isMerge ? 'merge' : 'sync',
    sourceBranchName: fullNames.sourceBranchName,
    destinationBranchName: fullNames.destinationBranchName,
  };
};

export const CompareBranchesSuccessFlag = compose<any, any, any>(
  injectIntl,
  connect(mapStateToProps)
)(SuccessFlag);
